import moment from 'moment-timezone';

/**
 * Align DoD data with current data timestamps so the two series overlay correctly.
 *
 * Strategy: derive the millisecond offset between the first DoD point and the
 * first current point, then for every current timestamp look up the DoD point
 * whose original timestamp equals (currentTimestamp - offset).  This is exact
 * regardless of the shortcut offset used (e.g. −24 h for "Last 6 hours").
 *
 * Missing DoD points are filled with 0 so the chart always renders a full,
 * time-aligned comparison line.
 */
export const alignDodDataWithCurrentData = (currentData: any[] = [], dodData: any[] = []) => {
  if (!currentData || currentData.length === 0) return [];
  if (!dodData || dodData.length === 0) {
    return currentData.map((p) => [p[0], null, null]);
  }

  // Build a ms-timestamp → value lookup for fast access
  const dodLookup = new Map<number, [any, any]>();
  dodData.forEach((point) => {
    const ts = moment(point[0]).valueOf();
    dodLookup.set(ts, point);
  });

  // Compute the offset: how many ms earlier the DoD window starts vs. current
  const currentStart = moment(currentData[0][0]).valueOf();
  const dodStart = moment(dodData[0][0]).valueOf();
  const offsetMs = currentStart - dodStart; // positive → DoD is in the past

  return currentData.map((currentPoint) => {
    const currentTs = moment(currentPoint[0]).valueOf();
    const expectedDodTs = currentTs - offsetMs;

    // Exact match by computed offset
    const dodPoint = dodLookup.get(expectedDodTs);
    if (dodPoint) {
      return [currentPoint[0], dodPoint[1] ?? null, dodPoint[0]];
    }

    // Nearest-neighbour fallback (handles sub-minute rounding differences)
    let nearest: [any, any] | null = null;
    let minDiff = Infinity;
    dodLookup.forEach((point, ts) => {
      const diff = Math.abs(ts - expectedDodTs);
      if (diff < minDiff) {
        minDiff = diff;
        nearest = point;
      }
    });

    // Accept nearest only if within half a granularity step (30 min tolerance)
    if (nearest && minDiff <= 30 * 60 * 1000) {
      return [currentPoint[0], (nearest as [any, any])[1] ?? null, (nearest as [any, any])[0]];
    }

    return [currentPoint[0], null, null];
  });
};

// Build DoD series similar to metrics-panel-hz pattern
export const buildDodSeries = (data: any[] = [], options: any = {}) => {
  const { xAxisIndex = 0, yAxisIndex = 0, barChart = false } = options;

  const dodColor = '#999999'; // Darker gray color for better visibility

  return {
    id: 'dod-series',
    name: 'Previous Period',
    data,
    type: barChart ? 'bar' : 'line',
    xAxisIndex,
    yAxisIndex,
    showSymbol: false,
    areaStyle: barChart
      ? null
      : {
          opacity: 0.15,
          color: dodColor,
        },
    lineStyle: barChart
      ? null
      : {
          width: 1.5,
          type: 'dashed' as any,
          color: dodColor,
        },
    itemStyle: barChart
      ? {
          color: dodColor,
          opacity: 0.6,
        }
      : null,
    z: 1, // Lower z-index than primary series
  };
};

// Format DoD series to be injected into existing chart options
export function formatDodSeries(option: any, data: any[] = [], barChart = false) {
  if (!data || data.length === 0) {
    return { series: [] };
  }

  const dodSeries = buildDodSeries(data, {
    xAxisIndex: 0,
    yAxisIndex: 0,
    barChart,
  });

  return { series: [dodSeries] };
}

// Update chart option to include DoD series with overlapping
export const updateChartWithDodSeries = (
  option: any,
  dodData: any[],
  barChart = false,
  currentData: any[] = [],
) => {
  const existingSeries = option.series || [];

  // Remove any existing DoD series
  const filteredSeries = existingSeries.filter((s: any) => s.id !== 'dod-series');

  if (currentData && currentData.length > 0) {
    // Align DoD data with current timestamps; missing points are filled with 0
    const alignedDodData = alignDodDataWithCurrentData(currentData, dodData);

    const dodSeries = buildDodSeries(alignedDodData, { barChart });

    // Ensure primary series renders above DoD
    const updatedSeries = filteredSeries.map((s: any, index: number) =>
      index === 0 ? { ...s, z: 2 } : s,
    );

    return {
      ...option,
      series: [dodSeries, ...updatedSeries], // DoD first (background), then primary
    };
  }

  return {
    ...option,
    series: filteredSeries,
  };
};
